import { ProgramStatus } from '../../constants'
import { findCountryProgram } from '../Programs/ProgramDetail/ProgramDetail.models'
import { OrgAddressDto, ProgramCatalogDto } from '../../types/swaggerTypes'

/**
 * CLOS-13391 — Shared copy for PTA enrolment restriction UI.
 * Used in PatientEnrollModal and UnenrolledProgramsInfo to keep wording consistent.
 */
export const PTA_ENROLMENT_RESTRICTED_MESSAGE =
  'Enrolment for this program is restricted'

/** CLOS-13742 — Copy for disabled PTA patient transfer (Squidex/i18n later). */
export const PTA_TRANSFER_RESTRICTED_MESSAGE =
  'Transfer is not available for post-trial access patients.'

/**
 * Backend may mask PTA patients with the literal initials "PTA".
 * CLOS-13893 — masked "PTA" must not be shown in the UI; treat as unavailable.
 */
export function areInitialsMaskedAsPta(
  initials: string | null | undefined
): boolean {
  return (initials ?? '').trim().toUpperCase() === 'PTA'
}

export const trimInitials = (initials: string | undefined | null): string => {
  if (!initials || initials === '') return '**'
  if (areInitialsMaskedAsPta(initials)) return '**'
  if (initials.length && initials.length < 3) return initials
  else return initials.slice(0, 2)
}

/**
 * Full initials for text columns (e.g. Associated Patients).
 * CLOS-13893 — hide Oracle-masked "PTA" without avatar-style truncation.
 */
export function getPatientInitialsFieldDisplay(
  initials: string | null | undefined
): string {
  if (areInitialsMaskedAsPta(initials)) return '**'
  return initials ?? ''
}

const canonicalPostTrialAccessOnly =
  ProgramStatus.PostTrialAccessOnly.toUpperCase()

/**
 * Normalizes program status strings from APIs that may use hyphenation
 * (e.g. APEX/Oracle `Post-Trial Access Only`) vs spaced canonical text.
 */
export function normalizeProgramStatusForComparison(
  programStatus: string | null | undefined
): string {
  return (programStatus ?? '')
    .trim()
    .toUpperCase()
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
}

/** True when status is Post Trial Access Only, including hyphenated variants. */
export function isPostTrialAccessOnlyProgramStatus(
  programStatus: string | null | undefined
): boolean {
  return (
    normalizeProgramStatusForComparison(programStatus) ===
    canonicalPostTrialAccessOnly
  )
}

/**
 * CLOS-14456 / CLOS-14457 — HCPs cannot create patients or enrol physicians on
 * PTA programmes. The PTA flag may be set at programme or country level.
 */
export function isPostTrialAccessOnlyProgram(
  program: ProgramCatalogDto | null | undefined
): boolean {
  return (
    isPostTrialAccessOnlyProgramStatus(program?.program?.programStatus) ||
    isPostTrialAccessOnlyProgramStatus(
      program?.programCountry?.countryProgramStatus
    )
  )
}

/**
 * Patient search rows sometimes omit or mis-set `programStatus`. When the list is
 * scoped to a program detail tab, pass `program.programCountry.countryProgramStatus`
 * so PTA masking matches the catalogue (same source as the PTA badge).
 */
export function isPatientSummaryPostTrialAccess(
  patientProgramStatus: string | null | undefined,
  programDetailCountryProgramStatus?: string | null,
  catalogProgramStatus?: string | null
): boolean {
  return (
    isPostTrialAccessOnlyProgramStatus(patientProgramStatus) ||
    isPostTrialAccessOnlyProgramStatus(programDetailCountryProgramStatus) ||
    isPostTrialAccessOnlyProgramStatus(catalogProgramStatus)
  )
}

/** Whether the patient row is PTA (for clinical-trial-ID swap / enrolment gates). */
export function shouldShowPatientPtaInitialsAvatar(
  programStatus: string | null | undefined,
  initials?: string | null,
  programDetailCountryProgramStatus?: string | null,
  catalogProgramStatus?: string | null
): boolean {
  return (
    isPatientSummaryPostTrialAccess(
      programStatus,
      programDetailCountryProgramStatus,
      catalogProgramStatus
    ) || areInitialsMaskedAsPta(initials)
  )
}

/**
 * Resolves country program status for the current institute (shipping country),
 * preferring the matching entry in `programCountries` over the singular `programCountry`.
 */
export function resolveCountryProgramStatusForInstitute(
  programCatalog: ProgramCatalogDto | undefined,
  defaultShippingAddress?: OrgAddressDto | null,
  /** Institute portfolio / shipping country when default address is not set. */
  instituteCountryCode?: string | null
): string | undefined {
  if (!programCatalog) {
    return undefined
  }

  const countryCode =
    defaultShippingAddress?.country?.trim() || instituteCountryCode?.trim()

  if (countryCode && programCatalog.programCountries?.length) {
    const countryProgram = findCountryProgram(programCatalog.programCountries, {
      country: countryCode
    } as OrgAddressDto)
    if (countryProgram?.countryProgramStatus) {
      return countryProgram.countryProgramStatus
    }
  }

  return programCatalog.programCountry?.countryProgramStatus
}

/** PTA check for patient detail: patient DTO, catalogue country, and global program status. */
export function isPatientDetailPostTrialAccess(
  patientProgramStatus: string | null | undefined,
  programCatalog: ProgramCatalogDto | undefined,
  defaultShippingAddress?: OrgAddressDto | null,
  instituteCountryCode?: string | null
): boolean {
  return isPatientSummaryPostTrialAccess(
    patientProgramStatus,
    resolveCountryProgramStatusForInstitute(
      programCatalog,
      defaultShippingAddress,
      instituteCountryCode
    ),
    programCatalog?.program?.programStatus
  )
}

/** Initials shown in patient list/dashboard avatar. */
export function getPatientInitialsAvatarLabel(
  initials: string | null | undefined
): string {
  return trimInitials(initials)
}

/** Patient detail header shows the avatar when there are initials or the program is PTA-only. */
export function shouldShowPatientDetailHeaderAvatar(
  initials: string | null | undefined,
  programStatus: string | null | undefined,
  programCountryStatus?: string | null
): boolean {
  return Boolean(
    initials ||
      shouldShowPatientPtaInitialsAvatar(
        programStatus,
        initials,
        programCountryStatus
      )
  )
}
